import { NavigateFunction } from "react-router-dom";
import DocumentReference, {
  BoundingBox,
  DocumentIndexType,
  normalizeDocumentIndex,
} from "@/models/conversation/document";
import { saveChatScrollStateFromDom } from "@/lib/chatScrollMemory";
import { logger } from "@/lib/logger";
import {
  buildAppNavigationUrl,
  getModifierKeyLabel,
  isModifierKeyPressed,
  isNavigableReference,
  openAppNavigation,
  resolveLegislationReferenceView,
  shouldOpenInNewTab,
} from "@/lib/navigation/appNavigation";

export { getModifierKeyLabel, isModifierKeyPressed };

/**
 * Gets the URL path for a document reference
 * @returns The URL path string or null if the reference is invalid
 */
export const getReferenceUrl = (
  reference: DocumentReference
): string | null => {
  return buildAppNavigationUrl({ kind: "documentReference", reference });
};

export type ReferenceNavigationStore = {
  setSelectedBoundingBoxes: (boundingBoxes: BoundingBox[] | undefined) => void;
  setSelectedDocumentReference: (
    documentReference: DocumentReference | undefined
  ) => void;
  setDocumentReferences: (documentReferences: DocumentReference[]) => void;
  resetWorkspaceDocumentVersionStore: () => void;
};

export type NavigateToReferenceOptions = {
  reference: DocumentReference;
  references: DocumentReference[];
  navigate: NavigateFunction;
  event?: React.MouseEvent | MouseEvent;
  store: ReferenceNavigationStore;
  onError?: (message: string) => void;
  onDocumentIndexTypeDocument?: () => void;
  conversationId?: string;
};

/**
 * Handles navigation to a document reference with support for CMD/CTRL + Click to open in new tab
 * @param conversationId - Optional conversation ID to save scroll position for restoration when navigating back
 */
export const navigateToReference = ({
  reference,
  references,
  navigate,
  event,
  store,
  onError,
  onDocumentIndexTypeDocument,
  conversationId,
}: NavigateToReferenceOptions): void => {
  const documentIndexForSwitch = normalizeDocumentIndex(
    reference.documentIndex ?? ""
  );

  if (!documentIndexForSwitch) {
    logger.warn("Chat citation has no known document index", {
      operation: "navigateToReference",
      documentId: reference.documentId,
      chunkId: reference.chunkId,
      documentIndex: reference.documentIndex,
    });
    onError?.("Reference blev ikke fundet");
    return;
  }

  if (documentIndexForSwitch === DocumentIndexType.Document) {
    onDocumentIndexTypeDocument?.();
    return;
  }

  if (!isNavigableReference(reference)) {
    logger.warn("Blocked chat citation without a usable documentId", {
      operation: "navigateToReference",
      documentId: reference.documentId,
      source: reference.source,
      chunkId: reference.chunkId,
      documentIndex: reference.documentIndex,
    });
    onError?.(
      "Kilden kunne ikke åbnes, da henvisningen mangler et dokument-id"
    );
    return;
  }

  const url = getReferenceUrl(reference);

  if (event && shouldOpenInNewTab(event) && url) {
    openAppNavigation({
      target: { kind: "path", path: url },
      navigate,
      event,
      onWorkspaceDocumentNavigate: () =>
        store.resetWorkspaceDocumentVersionStore(),
    });
    return;
  }

  if (conversationId) {
    saveChatScrollStateFromDom(conversationId);
  }

  switch (documentIndexForSwitch) {
    case DocumentIndexType.Retsinformation:
      if (resolveLegislationReferenceView(reference) === "html") {
        // The chat HTML viewer shows the conversation's references in its
        // sidebar, so it needs the same list the PDF viewer gets.
        store.setDocumentReferences(references);
        openAppNavigation({
          target: {
            kind: "documentReference",
            reference,
          },
          navigate,
        });
      } else {
        store.setSelectedBoundingBoxes(reference.boundingBoxes);
        store.setSelectedDocumentReference(reference);
        store.setDocumentReferences(references);
        openAppNavigation({
          target: {
            kind: "documentReference",
            reference,
          },
          navigate,
        });
      }
      break;

    case DocumentIndexType.Workspace:
      if (!reference.workspaceId) {
        logger.warn("Workspace chat citation has no workspace id", {
          operation: "navigateToReference",
          documentId: reference.documentId,
          chunkId: reference.chunkId,
        });
        onError?.("Reference blev ikke fundet");
        return;
      }

      if (reference.htmlIds && reference.htmlIds.length > 0) {
        openAppNavigation({
          target: {
            kind: "documentReference",
            reference,
          },
          navigate,
          onWorkspaceDocumentNavigate: () =>
            store.resetWorkspaceDocumentVersionStore(),
        });
      } else {
        store.setSelectedBoundingBoxes(reference.boundingBoxes);
        store.setSelectedDocumentReference(reference);
        store.setDocumentReferences(references);

        openAppNavigation({
          target: {
            kind: "documentReference",
            reference,
          },
          navigate,
          onWorkspaceDocumentNavigate: () =>
            store.resetWorkspaceDocumentVersionStore(),
        });
      }
      break;

    default:
      logger.warn("Chat citation has an unsupported document index", {
        operation: "navigateToReference",
        documentId: reference.documentId,
        chunkId: reference.chunkId,
        documentIndex: reference.documentIndex,
      });
      onError?.("Reference blev ikke fundet");
      break;
  }
};
